// Dated unavailability: table with Remove, and an add form that then shows the affected lessons with links to
// find replacements (SPEC §6.3, §7.5). Shared by teachers and classrooms.

import type { ApiResult } from "../api/client";
import type { NewPeriod } from "../api/teachers";
import type { AffectedLessons, Lesson, UnavailabilityPeriod, UnavailabilityReason } from "../api/types";
import { formatDate, t, translateCode } from "../i18n/i18n";
import { requireSchool } from "../session";
import { formatRange } from "../time";
import { createButton, createElement, createLink } from "./dom";
import { createErrorBox, showProblem } from "./feedback";
import { createCheckbox, createInputField, createSelectField, showFieldErrors, type Field } from "./forms";
import { lessonRoute } from "./lessonLabels";
import { createTimeRangePicker, type TimeRangePicker } from "./timeRangePicker";

export interface PeriodsSectionOptions {
  periods: UnavailabilityPeriod[];
  reasons: UnavailabilityReason[];
  canEdit: boolean;
  add: (period: NewPeriod) => Promise<ApiResult<AffectedLessons>>;
  remove: (periodId: string) => Promise<ApiResult<null>>;
  /** Called after a change; after an add it gets the affected lessons, so the page can show them. */
  onChanged: (affectedLessons: Lesson[] | null) => void;
}

export function buildPeriodsSection(options: PeriodsSectionOptions): HTMLElement {
  const section: HTMLElement = createElement("section", "card");
  section.append(createElement("h2", "", t("teachers.unavailability")), buildTable(options));
  if (options.canEdit) {
    section.append(buildAddForm(options));
  }

  return section;
}

function buildTable(options: PeriodsSectionOptions): HTMLElement {
  if (options.periods.length === 0) {
    return createElement("p", "muted", t("app.noPeriods"));
  }

  const table: HTMLTableElement = createElement("table");
  const head: HTMLTableRowElement = table.createTHead().insertRow();
  for (const title of [t("app.from"), t("app.to"), t("app.time"), t("app.reason"), t("app.note"), ""]) {
    head.append(createElement("th", "", title));
  }

  const body: HTMLTableSectionElement = table.createTBody();
  for (const period of options.periods) {
    body.append(buildRow(period, options));
  }

  return table;
}

function buildRow(period: UnavailabilityPeriod, options: PeriodsSectionOptions): HTMLTableRowElement {
  const row: HTMLTableRowElement = createElement("tr");
  const texts: string[] = [formatDate(period.dateFrom), formatDate(period.dateTo),
    period.range === null ? t("app.allDay") : formatRange(period.range),
    translateCode("unavailabilityReasons", period.reason), period.note ?? ""];
  for (const text of texts) {
    row.append(createElement("td", "", text));
  }

  const action: HTMLTableCellElement = createElement("td");
  if (options.canEdit) {
    action.append(createButton(t("app.remove"), async (): Promise<void> => {
      await options.remove(period.id);
      options.onChanged(null);
    }, "button-link"));
  }

  row.append(action);
  return row;
}

function buildAddForm(options: PeriodsSectionOptions): HTMLElement {
  const today: string = requireSchool().today;
  const from: Field<HTMLInputElement> = createInputField(t("app.from"), "date", today);
  const to: Field<HTMLInputElement> = createInputField(t("app.to"), "date", today);
  const allDay: Field<HTMLInputElement> = createCheckbox(t("app.allDay"), true);
  const picker: TimeRangePicker = createTimeRangePicker({ start: 32, end: 52 });
  picker.element.hidden = true;
  allDay.input.addEventListener("change", (): void => {
    picker.element.hidden = allDay.input.checked;
  });
  const reason: Field<HTMLSelectElement> = createSelectField(t("app.reason"), options.reasons.map(
    (code: string): { value: string; label: string } => ({
      value: code, label: translateCode("unavailabilityReasons", code),
    })), options.reasons[0] ?? "Other");
  const note: Field<HTMLInputElement> = createInputField(t("app.note"), "text", "");
  const result: HTMLDivElement = createElement("div");
  const save: HTMLButtonElement = createButton(t("teachers.addPeriod"), async (): Promise<void> => {
    const period: NewPeriod = {
      dateFrom: from.input.value, dateTo: to.input.value, range: allDay.input.checked ? null : picker.getRange(),
      reason: reason.input.value as UnavailabilityReason, note: note.input.value,
    };
    await submitPeriod(options, period, { result, fields: { dateFrom: from, dateTo: to, reason, note } });
  }, "button-primary");
  const form: HTMLDivElement = createElement("div", "form-grid");
  form.append(from.wrapper, to.wrapper, reason.wrapper, note.wrapper);
  const wrapper: HTMLDivElement = createElement("div");
  wrapper.append(createElement("h3", "", t("teachers.addPeriod")), form, allDay.wrapper, picker.element, save, result);
  return wrapper;
}

interface PeriodFormParts {
  result: HTMLElement;
  fields: Record<string, Field<HTMLElement>>;
}

async function submitPeriod(options: PeriodsSectionOptions, period: NewPeriod, parts: PeriodFormParts):
  Promise<void> {
  const response: ApiResult<AffectedLessons> = await options.add(period);
  showFieldErrors(response.ok ? null : response.problem, parts.fields);
  if (response.ok === false) {
    const box: HTMLDivElement = createErrorBox();
    showProblem(box, response.problem);
    parts.result.replaceChildren(box);
    return;
  }

  options.onChanged(response.data.affectedLessons);
}

/** "Lessons affected by this unavailability", each linking to its replacement search. */
export function buildAffectedLessons(lessons: Lesson[]): HTMLElement {
  const box: HTMLDivElement = createElement("div", "banner");
  if (lessons.length === 0) {
    box.append(t("teachers.noAffected"));
    return box;
  }

  const list: HTMLUListElement = createElement("ul");
  for (const lesson of lessons) {
    const item: HTMLLIElement = createElement("li");
    item.append(createLink(lessonRoute(lesson), `${formatDate(lesson.key.date)} ${formatRange(lesson.range)} ${
      lesson.groupName}`));
    list.append(item);
  }

  box.append(createElement("p", "", t("teachers.affectedTitle")), list);
  return box;
}
